import { memo, useCallback, useMemo, useState } from "react";

import { type AttributionFilter, FilterMode, type Positions } from "@doitintl/cmp-models";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";

import { allocationTxt } from "../../../../assets/texts/CloudAnalytics/allocation";
import FilterDialog from "../../../../Components/Dialogs/CloudAnalytics/FilterDialog";
import { getMode } from "../../../../Components/Dialogs/CloudAnalytics/utils";
import DimensionSelectionRow from "../../../../Components/DimensionSelectorWithOperator/DimensionSelectionRow";
import { type MetadataOption } from "../../../../types";
import { useFullScreen } from "../../../../utils/dialog";
import { getLetterForIndex } from "../../../../utils/string";
import { type KeyTypeValues } from "../../api";
import { FilterModeOption } from "../types";

const LETTER_SIZE_BIG = 30;
const LETTER_SIZE_SMALL = 22;

type LetterStyleType = {
  width: number;
  fontWeight: string;
  display?: string;
  alignItems?: string;
  justifyContent?: string;
  height?: number;
  background?: string;
  borderRadius?: string;
};

const styles = {
  letterStyleAttribution: {
    width: LETTER_SIZE_SMALL,
    fontWeight: "bold",
  },
  letterStyleGuidedExperience: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: LETTER_SIZE_BIG,
    height: LETTER_SIZE_BIG,
    fontWeight: "bold",
    background: "rgba(0, 0, 0, 0.12)",
    borderRadius: "50%",
  },
};

type Props = {
  initialFilters: AttributionFilter[];
  dimensions?: MetadataOption[];
  excludeSelectMetadataIds?: Set<string>;
  guidedExperienceMode?: boolean;
  isEditMode?: boolean;
  handleDimensionLabelsChange: (
    values: KeyTypeValues[],
    filter?: AttributionFilter,
    position?: Positions
  ) => Promise<MetadataOption | null>;
};

type DimensionSelectionRowWithLetterPrefixProps = {
  filter: AttributionFilter;
  letter: string;
  dimensions: MetadataOption[];
  options: MetadataOption[];
  disabled: boolean;
  excludeSelectMetadataIds?: Set<string>;
  letterStyle: LetterStyleType;
  onDimensionChanged: (dimension: MetadataOption) => void;
  onDimensionCleared: () => void;
  onEdit: (dimension: MetadataOption) => void;
};

const DimensionSelectionRowWithLetterPrefix = memo<DimensionSelectionRowWithLetterPrefixProps>(
  ({
    filter,
    letter,
    dimensions,
    options,
    disabled,
    excludeSelectMetadataIds,
    letterStyle,
    onDimensionChanged,
    onDimensionCleared,
    onEdit,
  }) => {
    const md = dimensions.find((md) => md.id === filter.id);
    if (!md) return null;
    return (
      <Grid container size={12}>
        <Grid
          sx={{
            alignContent: { md: "center", xs: "flex-start" },
            mb: 0.5,
            mt: { xs: 1, md: 0 },
          }}
        >
          <Box sx={letterStyle}>{letter}</Box>
        </Grid>
        <Grid
          size={{
            xs: "grow",
            md: "auto",
          }}
        >
          <DimensionSelectionRow
            onDimensionChanged={(dimension) => {
              if (dimension) {
                onDimensionChanged(dimension);
              } else {
                onDimensionCleared();
              }
            }}
            selectedDimension={md}
            dimensions={options}
            disabled={disabled}
            excludeSelectMetadataIds={excludeSelectMetadataIds}
            onEdit={() => {
              onEdit(md);
            }}
            filter={filter}
          />
        </Grid>
      </Grid>
    );
  }
);

DimensionSelectionRowWithLetterPrefix.displayName = "DimensionSelectionRowWithLetterPrefix";

const createFilterFromDimension = (md: MetadataOption): AttributionFilter => ({
  id: md.id,
  field: md.data.field,
  type: md.data.type,
  key: md.data.key,
  allowNull: false,
  inverse: false,
  regexp: null,
  values: null,
  mode: md.mode ?? FilterMode.IS,
});

export type DimensionEditOperation = {
  dimension: MetadataOption;
  newMode?: FilterModeOption;
  filterIndex?: number;
};

const getFilterModeValues = (mode: FilterModeOption) => ({
  inverse:
    mode === FilterModeOption.IS_NOT ||
    mode === FilterModeOption.DOES_NOT_CONTAIN ||
    mode === FilterModeOption.DOES_NOT_MATCHES_REGEX,
  mode: getMode(mode),
});

export default function useDimensionsSelection({
  isEditMode,
  initialFilters,
  dimensions = [],
  excludeSelectMetadataIds,
  guidedExperienceMode = false,
  handleDimensionLabelsChange,
}: Props) {
  const [filters, setFilters] = useState(initialFilters);
  const [editOperation, setEditOperation] = useState<DimensionEditOperation | null>(null);
  const [currentFilter, setCurrentFilter] = useState<AttributionFilter | null>(null);
  const { isMobile } = useFullScreen();
  const [disabled, setDisabled] = useState(false);
  const letterStyle = guidedExperienceMode ? styles.letterStyleGuidedExperience : styles.letterStyleAttribution;

  const variablesArray = useMemo(() => filters.map((_, index) => getLetterForIndex(index)), [filters]);
  const filteredDimensions = useMemo(
    () => dimensions.filter((md) => md._visible || filters.findIndex((f) => f.id === md.id) > -1) ?? [],
    [dimensions, filters]
  );

  const handleChangeDimensionFilters = useCallback(
    (type: FilterModeOption, value, inverse: boolean) => {
      if (!editOperation) {
        return;
      }
      setFilters((prevFilters) => {
        const mode = getMode(type);
        const dimension = editOperation.dimension;
        const newFilter = createFilterFromDimension(dimension);
        let removeFilter = false;
        newFilter.inverse = inverse;
        newFilter.mode = mode;
        if (mode === FilterMode.REGEXP) {
          newFilter.values = null;
          newFilter.regexp = value;
        } else {
          removeFilter = value?.length === 0;
          newFilter.values = value;
          newFilter.regexp = null;
          if (dimension.data.nullFallback) {
            const nullIndex = newFilter.values?.findIndex((v) => v === dimension.data.nullFallback) ?? -1;
            newFilter.allowNull = nullIndex !== -1;
            if (newFilter.allowNull) {
              // move the 'nullFallback' to the end of the values array
              newFilter.values?.splice(nullIndex, 1);
              newFilter.values?.push(dimension.data.nullFallback);
            }
          }
        }
        const newFilters = prevFilters.slice();

        const filterIndexToChange = editOperation.filterIndex ?? -1;

        if (filterIndexToChange === -1) {
          newFilter.mode = newFilter.mode ?? FilterMode.IS;
          newFilters.push(newFilter);
          return newFilters;
        }

        if (removeFilter) {
          newFilters.splice(filterIndexToChange, 1);
        } else {
          const existingFilter = filters[filterIndexToChange];
          const newModeValues = editOperation.newMode ? getFilterModeValues(editOperation.newMode) : {};
          newFilters[filterIndexToChange] = {
            ...existingFilter,
            ...newFilter,
            ...newModeValues,
          };
        }
        return newFilters;
      });
    },
    [editOperation, filters]
  );

  const handleCloseFilterDialog = useCallback(() => {
    setEditOperation(null);
    setCurrentFilter(null);
  }, [setEditOperation]);

  const handleEditDimensionFilters = useCallback(
    (dimension: MetadataOption, filterIndex: number) => {
      const filter = filters[filterIndex];
      setCurrentFilter(filter);
      const newDimension: MetadataOption = {
        ...dimension,
        _filter: filter?.values ?? null,
        _regexp: filter?.regexp ?? null,
      };
      setEditOperation({
        dimension: newDimension,
        filterIndex,
      });
    },
    [filters]
  );

  const handleDimensionCleared = useCallback((filterIndex: number) => {
    setFilters((prevItems) => prevItems.filter((_, i) => i !== filterIndex));
  }, []);

  const handleExistingDimensionChanged = useCallback((filterIndex: number, dimension: MetadataOption) => {
    setEditOperation({
      filterIndex,
      dimension,
    });
  }, []);

  const handleAddNewDimension = useCallback(
    async (dimension: MetadataOption | null) => {
      if (dimension) {
        const value = await handleDimensionLabelsChange([{ key: dimension.data.key, type: dimension.data.type }]);
        setEditOperation({
          dimension: value || dimension,
        });
      }
    },
    [handleDimensionLabelsChange]
  );

  const handleEditDimension = useCallback(
    async (dimension: MetadataOption, index: number) => {
      const value = await handleDimensionLabelsChange([{ key: dimension.data.key, type: dimension.data.type }]);
      handleEditDimensionFilters(value || dimension, index);
    },
    [handleDimensionLabelsChange, handleEditDimensionFilters]
  );
  const DimensionsSelection = useMemo(
    () => (
      <>
        <Grid container gap={1} alignContent="flex-start">
          {filters.map((filter, index) => (
            <DimensionSelectionRowWithLetterPrefix
              key={`filter-row-${variablesArray[index]}`}
              filter={filter}
              letter={variablesArray[index]}
              letterStyle={letterStyle}
              dimensions={dimensions}
              options={filteredDimensions}
              disabled={disabled || isMobile}
              excludeSelectMetadataIds={excludeSelectMetadataIds}
              onDimensionChanged={(dimension: MetadataOption) => {
                handleExistingDimensionChanged(index, dimension);
              }}
              onDimensionCleared={() => {
                handleDimensionCleared(index);
              }}
              onEdit={(dimension) => handleEditDimension(dimension, index)}
            />
          ))}

          <Grid container spacing={filters.length > 0 ? 1 : 0} wrap="nowrap" sx={{ alignItems: "center" }} size={12}>
            {!isMobile && !guidedExperienceMode && filters.length > 0 && <Grid sx={{ width: letterStyle.width - 8 }} />}
            {!disabled && !isMobile && (
              <Grid display={{ xs: "none", md: "flex" }} size={{ xs: "grow", md: "auto" }}>
                <DimensionSelectionRow
                  selectedDimension={null}
                  dimensions={filteredDimensions}
                  disabled={false}
                  placeholder={allocationTxt.ADD_DIMENSION}
                  onEdit={() => {}}
                  excludeSelectMetadataIds={excludeSelectMetadataIds}
                  label={allocationTxt.ADD_DIMENSION}
                  onDimensionChanged={handleAddNewDimension}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        {editOperation && (
          <FilterDialog
            open={true}
            {...(isEditMode ? { alertSeverity: "warning", alertText: allocationTxt.ALLOCATION_EDIT_FILTER } : {})}
            filter={currentFilter}
            selected={editOperation.dimension}
            onSave={handleChangeDimensionFilters}
            onClose={handleCloseFilterDialog}
            onCancel={handleCloseFilterDialog}
          />
        )}
      </>
    ),
    [
      filters,
      isMobile,
      guidedExperienceMode,
      letterStyle,
      disabled,
      filteredDimensions,
      excludeSelectMetadataIds,
      handleAddNewDimension,
      editOperation,
      isEditMode,
      currentFilter,
      handleChangeDimensionFilters,
      handleCloseFilterDialog,
      variablesArray,
      dimensions,
      handleExistingDimensionChanged,
      handleDimensionCleared,
      handleEditDimension,
    ]
  );

  return { DimensionsSelection, filters, setFilters, setDisabled };
}
